import React from 'react';
import { Ban, Check, KeyRound, Lock, ShieldCheck, Trash2, X } from 'lucide-react';
import {
  solutionsFaq,
  solutionsFooter,
  solutionsHowItWorks,
  solutionsObjections,
  solutionsSecurity,
  solutionsTrust,
} from '../../../shared/config/solutionsContent';

/**
 * Trust & content sections for the Solutions page (RLX-FEAT-007).
 * Pure presentational — shared verbatim by the SPA and the prerendered
 * static twin so marketing copy can never drift between the two.
 * All copy lives in `src/shared/config/solutionsContent.ts`.
 */

/** Proof block between the integrations strip and the master video. */
export function SolutionsTrustBlock(): React.ReactElement {
  return (
    <section className="solutions-trust" aria-labelledby="solutions-trust-heading">
      <header className="solutions-section-header solutions-section-header--centered">
        <span className="solutions-section-eyebrow">{solutionsTrust.eyebrow}</span>
        <h2 id="solutions-trust-heading">{solutionsTrust.heading}</h2>
        <p className="solutions-section-sub">{solutionsTrust.sub}</p>
      </header>
      <ul className="solutions-trust__badges" aria-label="Why trust Relix">
        {solutionsTrust.badges.map((badge) => (
          <li key={badge} className="solutions-trust__badge">
            <ShieldCheck size={15} strokeWidth={1.8} aria-hidden="true" />
            <span>{badge}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * 3-step "how it works" (RLX-FEAT-008) — pairs with the metro-map:
 * the map shows the signal journey, this block states the user journey.
 */
export function SolutionsHowItWorks(): React.ReactElement {
  return (
    <section className="solutions-steps" aria-labelledby="solutions-steps-heading">
      <header className="solutions-section-header solutions-section-header--centered">
        <span className="solutions-section-eyebrow">{solutionsHowItWorks.eyebrow}</span>
        <h2 id="solutions-steps-heading">{solutionsHowItWorks.heading}</h2>
      </header>
      <ol className="solutions-steps__list">
        {solutionsHowItWorks.steps.map((step) => (
          <li key={step.num} className="solutions-steps__step">
            <span className="solutions-steps__num" aria-hidden="true">
              {step.num}
            </span>
            <h3 className="solutions-steps__title">{step.title}</h3>
            <p className="solutions-steps__body">{step.body}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * Objection handling — "why not just…". Versus layout: the muted ✕ line is
 * the status quo, the violet ✓ line is the Relix punchline. One short
 * sentence each — the contrast is the hook.
 */
export function SolutionsObjectionsSection(): React.ReactElement {
  return (
    <section className="solutions-objections" aria-labelledby="solutions-objections-heading">
      <header className="solutions-section-header solutions-section-header--centered">
        <span className="solutions-section-eyebrow">{solutionsObjections.eyebrow}</span>
        <h2 id="solutions-objections-heading">{solutionsObjections.heading}</h2>
      </header>
      <div className="solutions-objections__grid">
        {solutionsObjections.items.map((item) => (
          <article key={item.question} className="solutions-objections__card">
            <h3 className="solutions-objections__question">{item.question}</h3>
            <div className="solutions-objections__versus">
              <p className="solutions-objections__side solutions-objections__side--them">
                <X size={14} strokeWidth={2} aria-hidden="true" />
                <span>{item.them}</span>
              </p>
              <p className="solutions-objections__side solutions-objections__side--relix">
                <Check size={14} strokeWidth={2.2} aria-hidden="true" />
                <span>{item.relix}</span>
              </p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

const SECURITY_STRIP_ICONS = {
  key: KeyRound,
  ban: Ban,
  lock: Lock,
  trash: Trash2,
} as const;

/**
 * Security & data + plain-language privacy note.
 *
 * `variant="strip"` (Solutions page): compact icon trust-bar, no sub —
 * scannable in seconds. `variant="cards"` (/help): detailed cards with the
 * full explanations. Anchor ids are props so the Help page can own
 * `/help#security` / `/help#privacy` while Solutions keeps its legacy anchors.
 */
export function SolutionsSecuritySection({
  variant = 'cards',
  sectionId = 'solutions-security',
  privacyId = 'solutions-privacy',
}: {
  variant?: 'cards' | 'strip';
  sectionId?: string;
  privacyId?: string;
}): React.ReactElement {
  return (
    <section
      id={sectionId}
      className={`solutions-security${variant === 'strip' ? ' solutions-security--strip' : ''}`}
      aria-labelledby={`${sectionId}-heading`}
    >
      <header className="solutions-section-header solutions-section-header--centered">
        <span className="solutions-section-eyebrow">{solutionsSecurity.eyebrow}</span>
        <h2 id={`${sectionId}-heading`}>{solutionsSecurity.heading}</h2>
        {variant === 'cards' ? (
          <p className="solutions-section-sub">{solutionsSecurity.sub}</p>
        ) : null}
      </header>
      {variant === 'strip' ? (
        <ul className="solutions-security__strip">
          {solutionsSecurity.strip.map((item) => {
            const Icon = SECURITY_STRIP_ICONS[item.icon];
            return (
              <li key={item.title} className="solutions-security__strip-item">
                <span className="solutions-security__strip-icon">
                  <Icon size={18} strokeWidth={1.8} aria-hidden="true" />
                </span>
                <span className="solutions-security__strip-title">{item.title}</span>
                <span className="solutions-security__strip-caption">{item.caption}</span>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="solutions-security__grid">
          {solutionsSecurity.items.map((item) => (
            <article key={item.title} className="solutions-security__card">
              <h3 className="solutions-security__title">{item.title}</h3>
              <p className="solutions-security__body">{item.body}</p>
            </article>
          ))}
        </div>
      )}
      <p id={privacyId} className="solutions-security__privacy">
        {solutionsSecurity.privacyNote}
      </p>
    </section>
  );
}

/**
 * FAQ as native <details> accordions — accessible, keyboard-operable, and
 * fully readable in the no-JS prerendered HTML (crawlers see the answers).
 *
 * Default (Solutions page): only `scope: 'both'` items + a link to /help.
 * `full` (Help page): every item, no more-link.
 */
export function SolutionsFaqSection({ full = false }: { full?: boolean }): React.ReactElement {
  const items = full
    ? solutionsFaq.items
    : solutionsFaq.items.filter((item) => item.scope !== 'help');
  return (
    <section className="solutions-faq" aria-labelledby="solutions-faq-heading">
      <header className="solutions-section-header solutions-section-header--centered">
        <span className="solutions-section-eyebrow">{solutionsFaq.eyebrow}</span>
        <h2 id="solutions-faq-heading">{solutionsFaq.heading}</h2>
      </header>
      <div className="solutions-faq__list">
        {items.map((item) => (
          <details key={item.question} className="solutions-faq__item">
            <summary className="solutions-faq__question">{item.question}</summary>
            <p className="solutions-faq__answer">{item.answer}</p>
          </details>
        ))}
      </div>
      {!full ? (
        <p className="solutions-faq__more">
          <a href="/help">{solutionsFaq.moreLabel}</a>
        </p>
      ) : null}
    </section>
  );
}

/** Marketing footer — plain anchors so the static twin exposes real outgoing links. */
export function SolutionsFooter(): React.ReactElement {
  return (
    <footer className="solutions-footer" aria-label="Footer">
      <div className="solutions-footer__inner">
        <div className="solutions-footer__brand">
          <span className="solutions-footer__name">Relix</span>
          <span className="solutions-footer__tagline">{solutionsFooter.tagline}</span>
        </div>
        <nav className="solutions-footer__nav" aria-label="Footer navigation">
          {solutionsFooter.links.map((link) => (
            <a
              key={link.label}
              href={link.href}
              {...(link.href.startsWith('http')
                ? { target: '_blank', rel: 'noopener noreferrer' }
                : {})}
            >
              {link.label}
            </a>
          ))}
        </nav>
        <span className="solutions-footer__copy">{solutionsFooter.copyright}</span>
      </div>
    </footer>
  );
}
